import { Box, Grid, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { displaySum } from "../../common/formatting/displaySum";
import { HorizontalScrollBox } from "../../common/HorizontalScrollBox";
import { useProcurementTerms } from "../../common/terminology/useProcurementTerms";
import { PlanEntryCard } from "./PlanEntryCard";
import {
  BOARD_COLUMNS,
  byBoardColumn,
  planTotals,
  type BoardColumn,
  type PlanEntry,
} from "./procurementPlanEntries";

const COLUMN_WIDTH = "11rem";

export const PlanBoard = ({ entries }: { entries: PlanEntry[] }) => {
  const { t } = useTranslation();
  const terms = useProcurementTerms();
  const columns = byBoardColumn(entries);
  const titles: Record<BoardColumn, string> = {
    withProcurer: t("With procurement"),
    planned: t("Planned"),
    draft: t("Draft"),
    published: terms.publishedStatus,
    evaluation: t("Evaluation and negotiation"),
  };
  return (
    <HorizontalScrollBox pb="2" data-testid="plan-board" scrollerProps={{ maxH: "75vh", overflowY: "auto" }}>
      <Grid templateColumns={`repeat(${BOARD_COLUMNS.length}, minmax(${COLUMN_WIDTH}, 1fr))`} columnGap="3" rowGap="2">
        <GroupHeading span={2}>{t("Needs without a procurement")}</GroupHeading>
        <GroupHeading span={3}>{t("Procurements")}</GroupHeading>
        {BOARD_COLUMNS.map((column) => (
          <BoardColumnView key={column} column={column} title={titles[column]} entries={columns[column]} />
        ))}
      </Grid>
    </HorizontalScrollBox>
  );
};

const GroupHeading = ({ span, children }: { span: number; children: string }) => (
  <Text
    gridColumn={`span ${span}`}
    fontSize="xs"
    fontWeight="semibold"
    textTransform="uppercase"
    color="smMuted"
    borderBottomWidth="1px"
    borderColor="smBorder"
    pb="1"
  >
    {children}
  </Text>
);

const BoardColumnView = ({ column, title, entries }: { column: BoardColumn; title: string; entries: PlanEntry[] }) => {
  const { t } = useTranslation();
  return (
    <Stack
      as="section"
      aria-label={title}
      spacing="2"
      bg="smBackgroundSecondary"
      borderRadius="md"
      p="2"
      minW="0"
      data-testid={`plan-column-${column}`}
    >
      <Box px="1" py="1" position="sticky" top="0" zIndex="1" bg="smBackgroundSecondary" data-testid={`plan-column-${column}-heading`}>
        <Text fontWeight="semibold" color="smText">
          {title}
        </Text>
        <Text fontSize="sm" color="smMuted" data-testid={`plan-column-${column}-total`}>
          {t("{{count}} entries · {{sum}}", {
            count: entries.length,
            sum: withCurrencyKeptOnTheLine(displaySum({ value: planTotals(entries).sum, currency: "NOK" })),
          })}
        </Text>
      </Box>
      {entries.map((entry) => (
        <PlanEntryCard key={entry.key} entry={entry} variant="board" />
      ))}
    </Stack>
  );
};

const withCurrencyKeptOnTheLine = (sum: string) => sum.replace(/ (?=\S+$)/, "\u00a0");
